import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { createCooperativeCalibration, stampCameraDeviceId } from '../../interaction/calibration';
import { CalibrationTrainer } from './CalibrationTrainer';
import { evaluateReadiness } from '../../interaction/camera';
import { INVENTED_CALIBRATION_COPY } from '../../platform/config/calibration';
import { INVENTED_COOPERATIVE_CALIBRATION_COPY } from '../../platform/config/cooperative-calibration';
import { useAppServices } from '../shell/services-context';

/**
 * Hero training for two (CAM-02 + CAM-07, GDS §13) — React-owned, like the solo trainer, because
 * this is shell work rather than gameplay (architecture §6).
 *
 * It is deliberately a **view over one machine**. `createCooperativeCalibration` sequences the two
 * individual trainings and the cooperative check; the Slice B′ lane resolver inside it decides whose
 * body is whose. This file holds no opinion about either — it renders phases, statuses and feedback
 * codes, and never names a frame or a landmark (architecture §11, §25). The two individual steps are
 * the *same* `CalibrationTrainer` a single child sees, so a prompt cannot be worded one way for one
 * hero and another way for two.
 *
 * **It refuses to do anything outside a cooperative sitting.** Cooperative camera play is
 * Full-Motion only for the MVP (D-076(4)) and two heroes are chosen on the grown-up screen; a
 * screen that trained one child into a two-lane profile would be a half-state, which is the thing
 * the readiness notes exist to avoid.
 *
 * **Where it hands over (Slice D′):** two trained heroes leave this screen with the camera turned on
 * for the sitting, into the ordinary cooperative mission. The screen decides nothing about that
 * mission — the stage still checks both profiles before it will read a body as an answer, and the
 * keyboard never stops working (CAM-09).
 */
export function CooperativeCalibrationPage() {
  const { camera, tunables, narration, sessionLaunchConfig, setCameraInputEnabled } =
    useAppServices();
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);

  const cooperative = sessionLaunchConfig?.playerMode === 'COOP_SIMULTANEOUS_SIMPLE';

  /**
   * The celebration's words, spoken through the sitting's narration service (MVP notes §18).
   *
   * Team-framed and about nobody in particular — two children are standing side by side, and the
   * one thing this moment may not do is separate them (ARCH §12, COOP-07).
   */
  const onCelebration = useCallback(() => {
    void narration.speak(INVENTED_COOPERATIVE_CALIBRATION_COPY.celebrationSpoken, {
      interrupt: true,
    });
  }, [narration]);

  const coop = useMemo(
    () =>
      createCooperativeCalibration({
        viewport: { width: window.innerWidth, height: window.innerHeight },
        frames: camera.poseFrames,
        // The finished profiles belong to the sitting, and to nothing that outlives it (GDS §13).
        onProfile: (profile) => camera.calibration.save(stampCameraDeviceId(profile, camera)),
        onCelebration,
      }),
    [camera, onCelebration],
  );

  const state = useSyncExternalStore(
    coop.subscribe,
    () => coop.state,
    () => coop.state,
  );
  const snapshot = useSyncExternalStore(camera.subscribe, camera.getSnapshot, camera.getSnapshot);
  const activeFlow = coop.activeFlow;
  const flowState = useSyncExternalStore(
    activeFlow?.subscribe ?? (() => () => {}),
    () => activeFlow?.state ?? null,
    () => activeFlow?.state ?? null,
  );

  useEffect(() => {
    if (!cooperative) return;
    const gameplayCadenceMs = Math.round(1_000 / tunables.get().pose.poseTargetFps);

    // Two children need two bodies looked for (D-077(1)): the trainer has to *see* the waiting
    // child in order to tolerate them rather than measure them. Asked before the camera starts.
    camera.requestMaxPoses(2);
    camera.attach(videoRef.current);
    camera.setSampleIntervalMs(gameplayCadenceMs);
    void camera.start();
    coop.attach();
    coop.start();

    return () => {
      coop.detach();
      coop.cancel();
      camera.setSampleIntervalMs(null);
      camera.stop();
      camera.attach(null);
    };
  }, [camera, coop, cooperative, tunables]);

  if (!cooperative) {
    return (
      <section data-testid="coop-calibration" data-phase="unavailable">
        <h1>{INVENTED_COOPERATIVE_CALIBRATION_COPY.pageTitle}</h1>
        <div className="panel" data-testid="coop-calibration-unavailable">
          <p>{INVENTED_COOPERATIVE_CALIBRATION_COPY.cooperativeOnly}</p>
          <p>
            <Link to="/" className="button">
              {INVENTED_COOPERATIVE_CALIBRATION_COPY.done}
            </Link>
          </p>
        </div>
      </section>
    );
  }

  const report = evaluateReadiness(snapshot.readings, { cooperative: true });
  // Body-position guidance in the readiness screen's own words: standing in the dark is the same
  // problem here as it is there, and a second vocabulary for it would teach a family two names.
  const guidance = report.ready || report.blocked ? null : report.headline;

  const turn =
    state.phase === 'hero-2'
      ? INVENTED_COOPERATIVE_CALIBRATION_COPY.heroTwoTraining
      : INVENTED_COOPERATIVE_CALIBRATION_COPY.heroOneTraining;
  const verification = state.verification.find((step) => step.status === 'active') ?? null;

  return (
    <section data-testid="coop-calibration" data-phase={state.phase}>
      <h1>{INVENTED_COOPERATIVE_CALIBRATION_COPY.pageTitle}</h1>
      <p className="lede">{INVENTED_COOPERATIVE_CALIBRATION_COPY.pageLede}</p>

      <div className="camera-check__stage calibration__stage">
        <video
          ref={videoRef}
          className="camera-check__feed"
          data-testid="camera-feed"
          data-mirrored="true"
          autoPlay
          playsInline
          muted
        />
        {/* Placeholder geometry, not artwork: the lane guides and player rings of MVP notes §16.
            Final art arrives through the theme manifest in Phase 5 and is never named here. */}
        <div className="calibration__lane-guide" data-testid="coop-lane-guide-left" />
        <div className="calibration__lane-guide" data-testid="coop-lane-guide-right" />
      </div>

      {state.phase === 'complete' ? (
        <div className="panel" data-testid="coop-calibration-complete">
          <h2>{INVENTED_COOPERATIVE_CALIBRATION_COPY.completeTitle}</h2>
          <p>{INVENTED_COOPERATIVE_CALIBRATION_COPY.completeBody}</p>
          <p className="notice">{INVENTED_COOPERATIVE_CALIBRATION_COPY.missionWithCamera}</p>
          <p>
            {gameId === undefined ? null : (
              /*
               * The mission handoff, made real (Slice D′).
               *
               * Until this slice the control navigated to a mission that was always going to run on
               * the keys, because nothing turned camera input on for a cooperative sitting — the
               * honest interim state D-078(2) tracked. This is the same handoff the solo trainer has
               * had since Slice D: it only *asks*, and the stage still checks both heroes' profiles
               * before it will believe a camera can answer anything (CAM-09 keeps the keys alive
               * either way).
               */
              <button
                type="button"
                className="button button--primary"
                data-testid="coop-calibration-play-with-camera"
                onClick={() => {
                  setCameraInputEnabled(true);
                  void navigate(`/play/${gameId}`);
                }}
              >
                {INVENTED_CALIBRATION_COPY.playWithCamera}
              </button>
            )}{' '}
            <Link to="/" className="button">
              {INVENTED_COOPERATIVE_CALIBRATION_COPY.done}
            </Link>
          </p>
        </div>
      ) : null}

      {activeFlow !== null && flowState !== null ? (
        <>
          <p data-testid="coop-calibration-turn" role="status" aria-live="polite">
            {turn}
          </p>
          <p className="readiness__message" data-testid="coop-calibration-waiting">
            {INVENTED_COOPERATIVE_CALIBRATION_COPY.waitingHero}
          </p>
          <CalibrationTrainer flow={activeFlow} state={flowState} guidance={guidance}>
            <Link to="/" className="button">
              {INVENTED_COOPERATIVE_CALIBRATION_COPY.stop}
            </Link>
          </CalibrationTrainer>
        </>
      ) : null}

      {state.phase === 'verification' ? (
        <div data-testid="coop-verification">
          <h2>{INVENTED_COOPERATIVE_CALIBRATION_COPY.verificationTitle}</h2>
          <p
            className="camera-check__headline"
            data-testid="coop-verification-prompt"
            role="status"
            aria-live="polite"
          >
            {verification === null
              ? ''
              : INVENTED_COOPERATIVE_CALIBRATION_COPY.prompts[verification.id]}
          </p>
          <p
            className="readiness__message"
            data-testid="coop-verification-feedback"
            role="status"
            aria-live="polite"
          >
            {verification === null
              ? ''
              : INVENTED_COOPERATIVE_CALIBRATION_COPY.feedback[verification.feedback]}
          </p>
          <ul className="readiness" data-testid="coop-verification-steps">
            {state.verification.map((step) => (
              <li
                key={step.id}
                data-testid="coop-verification-step"
                data-step-id={step.id}
                data-status={step.status}
              >
                <span className="readiness__label">{step.label}</span>
              </li>
            ))}
          </ul>
          <p>
            <Link to="/" className="button">
              {INVENTED_COOPERATIVE_CALIBRATION_COPY.stop}
            </Link>
          </p>
        </div>
      ) : null}
    </section>
  );
}
